/**
 * B2B Pricing Auto-Select
 * Automatically selects the correct pricingType variant based on customer tags
 * - B2B customers (tagged with "B2B") get "Wh" (Wholesale) variant
 * - Regular customers get "St" (Standard) variant
 */

(function() {
  'use strict';

  // Check if customer has B2B tag
  function isB2BCustomer(productInfoElement) {
    const isLoggedIn = productInfoElement.dataset.customerLoggedIn === 'true';
    if (!isLoggedIn) return false;
    
    const customerTags = productInfoElement.dataset.customerTags || '';
    return customerTags.split(',').some(tag => tag.trim() === 'B2B');
  }

  // Find the pricingType option fieldset/div
  function findPricingTypeOption(variantSelectsElement) {
    if (!variantSelectsElement) return null;

    // Look for fieldset or div containing pricingType option
    const optionElements = variantSelectsElement.querySelectorAll('fieldset, .product-form__input');
    
    for (const element of optionElements) {
      const legend = element.querySelector('legend');
      const label = element.querySelector('label');
      const optionName = legend?.textContent?.trim() || label?.textContent?.trim() || '';
      
      // Check if this is the pricingType option (remove colon if present)
      if (optionName.replace(':', '').trim() === 'pricingType') {
        return element;
      }
    }
    
    return null;
  }

  // Find all inputs/selects for pricingType option
  function findPricingTypeInputs(pricingTypeOption) {
    if (!pricingTypeOption) return { radioInputs: [], selectElement: null };

    const radioInputs = Array.from(pricingTypeOption.querySelectorAll('input[type="radio"]'));
    const selectElement = pricingTypeOption.querySelector('select');

    return { radioInputs, selectElement };
  }

  // Select a pricingType variant value (Wh or St)
  function selectPricingTypeValue(variantSelectsElement, value) {
    const pricingTypeOption = findPricingTypeOption(variantSelectsElement);
    if (!pricingTypeOption) {
      console.log('B2B Pricing: pricingType option not found, skipping auto-selection');
      return false;
    }

    const { radioInputs, selectElement } = findPricingTypeInputs(pricingTypeOption);

    let selected = false;

    // Handle radio inputs (button/swatch picker type)
    if (radioInputs.length > 0) {
      const targetInput = radioInputs.find(input => input.value === value);
      if (targetInput && !targetInput.disabled && !targetInput.classList.contains('disabled')) {
        targetInput.checked = true;
        // Trigger change event to notify VariantSelects
        targetInput.dispatchEvent(new Event('change', { bubbles: true }));
        selected = true;
      } else {
        // Fallback: if Wh is not available, try St
        if (value === 'Wh') {
          const fallbackInput = radioInputs.find(input => input.value === 'St' && !input.disabled && !input.classList.contains('disabled'));
          if (fallbackInput) {
            fallbackInput.checked = true;
            fallbackInput.dispatchEvent(new Event('change', { bubbles: true }));
            selected = true;
            console.log('B2B Pricing: Wh variant unavailable, falling back to St');
          }
        }
      }
    }
    // Handle select dropdown
    else if (selectElement) {
      const targetOption = Array.from(selectElement.options).find(option => option.value === value);
      if (targetOption && !targetOption.disabled) {
        selectElement.value = value;
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
        selected = true;
      } else {
        // Fallback: if Wh is not available, try St
        if (value === 'Wh') {
          const fallbackOption = Array.from(selectElement.options).find(option => option.value === 'St' && !option.disabled);
          if (fallbackOption) {
            selectElement.value = 'St';
            selectElement.dispatchEvent(new Event('change', { bubbles: true }));
            selected = true;
            console.log('B2B Pricing: Wh variant unavailable, falling back to St');
          }
        }
      }
    }

    return selected;
  }

  // Initialize B2B pricing auto-selection
  function initializeB2BPricing(productInfoElement) {
    if (!productInfoElement) return;

    const variantSelectsElement = productInfoElement.querySelector('variant-selects');
    if (!variantSelectsElement) {
      console.log('B2B Pricing: variant-selects element not found');
      return;
    }

    // Check if pricingType option exists
    const pricingTypeOption = findPricingTypeOption(variantSelectsElement);
    if (!pricingTypeOption) {
      // Product doesn't have pricingType variant - this is okay, just skip
      return;
    }

    // Determine which variant to select
    const shouldSelectWh = isB2BCustomer(productInfoElement);
    const targetValue = shouldSelectWh ? 'Wh' : 'St';

    // Select the appropriate variant
    const selected = selectPricingTypeValue(variantSelectsElement, targetValue);
    
    if (selected) {
      console.log(`B2B Pricing: Auto-selected ${targetValue} variant for ${shouldSelectWh ? 'B2B' : 'regular'} customer`);
    }
  }

  // Ensure pricingType selection persists when other variants change
  function maintainPricingTypeSelection(productInfoElement) {
    if (!productInfoElement) return;

    const variantSelectsElement = productInfoElement.querySelector('variant-selects');
    if (!variantSelectsElement) return;

    const pricingTypeOption = findPricingTypeOption(variantSelectsElement);
    if (!pricingTypeOption) return;

    const shouldSelectWh = isB2BCustomer(productInfoElement);
    const targetValue = shouldSelectWh ? 'Wh' : 'St';

    // Check if pricingType is still correctly selected
    const { radioInputs, selectElement } = findPricingTypeInputs(pricingTypeOption);
    
    let isCorrectlySelected = false;

    if (radioInputs.length > 0) {
      const checkedInput = radioInputs.find(input => input.checked);
      isCorrectlySelected = checkedInput && checkedInput.value === targetValue;
    } else if (selectElement) {
      isCorrectlySelected = selectElement.value === targetValue;
    }

    // If not correctly selected, re-apply selection
    if (!isCorrectlySelected) {
      selectPricingTypeValue(variantSelectsElement, targetValue);
    }
  }

  // Get currently selected variant from the variant-selects element
  function getCurrentSelectedVariant(variantSelectsElement) {
    if (!variantSelectsElement) return null;
    
    const variantDataElement = variantSelectsElement.querySelector('[data-selected-variant]');
    if (!variantDataElement || !variantDataElement.textContent) return null;
    
    try {
      return JSON.parse(variantDataElement.textContent);
    } catch (e) {
      console.error('B2B Pricing: Error parsing variant data', e);
      return null;
    }
  }

  // Check if variant has pricingType: Wh
  function variantHasWhPricingType(variant) {
    if (!variant || !variant.options) return false;
    
    // Check all option values for "Wh"
    return variant.options.some(option => option === 'Wh');
  }

  // Validate and correct variant before form submission
  function validateVariantBeforeSubmit(productFormElement, productInfoElement, event) {
    return new Promise((resolve) => {
      const variantSelectsElement = productInfoElement.querySelector('variant-selects');
      if (!variantSelectsElement) {
        resolve(true); // No variant selector, allow submission
        return;
      }

      const currentVariant = getCurrentSelectedVariant(variantSelectsElement);
      if (!currentVariant) {
        resolve(true); // No variant data, allow submission
        return;
      }

      // Check if variant has Wh pricingType
      const hasWhPricing = variantHasWhPricingType(currentVariant);
      const isB2B = isB2BCustomer(productInfoElement);

      // If variant has Wh but customer is not B2B, correct it
      if (hasWhPricing && !isB2B) {
        console.log('B2B Pricing: Non-B2B customer attempted to add Wh variant, correcting to St');
        
        // Prevent the form submission temporarily
        event.preventDefault();
        event.stopImmediatePropagation();
        
        const form = productFormElement.querySelector('form');
        if (!form) {
          resolve(true);
          return;
        }

        // Select St variant
        const corrected = selectPricingTypeValue(variantSelectsElement, 'St');
        
        if (corrected) {
          // Flag to prevent handleVariantChange from running multiple times
          let hasResubmitted = false;
          
          // Wait for variant change to propagate and update the form
          const handleVariantChange = () => {
            // Prevent multiple resubmissions
            if (hasResubmitted) return;
            hasResubmitted = true;
            
            // Verify the variant was updated
            setTimeout(() => {
              const newVariant = getCurrentSelectedVariant(variantSelectsElement);
              if (newVariant && !variantHasWhPricingType(newVariant)) {
                // Update the hidden variant ID input in the form
                const variantIdInput = form.querySelector('input[name="id"]');
                if (variantIdInput && newVariant.id) {
                  variantIdInput.value = newVariant.id;
                  console.log('B2B Pricing: Variant corrected successfully, updated form variant ID to', newVariant.id);
                }
                
                // Mark form as resubmitting to skip validation on next submit
                resubmittingForms.add(form);
                
                // Now submit the form programmatically
                // Trigger the submit button click to go through product-form.js handler
                const submitButton = productFormElement.querySelector('[type="submit"]');
                if (submitButton && !submitButton.disabled && submitButton.getAttribute('aria-disabled') !== 'true') {
                  submitButton.click();
                } else {
                  form.requestSubmit();
                }
                resolve(true);
              } else {
                // Fallback: if correction didn't work, still allow but log warning
                console.warn('B2B Pricing: Could not verify variant correction, resubmitting form');
                
                // Mark form as resubmitting to skip validation on next submit
                resubmittingForms.add(form);
                
                const submitButton = productFormElement.querySelector('[type="submit"]');
                if (submitButton && !submitButton.disabled && submitButton.getAttribute('aria-disabled') !== 'true') {
                  submitButton.click();
                } else {
                  form.requestSubmit();
                }
                resolve(true);
              }
            }, 200);
          };

          // Listen for variant change via pub/sub
          let unsubscribe = null;
          if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              if (event.data?.sectionId === variantSelectsElement.dataset.section) {
                if (unsubscribe) unsubscribe();
                handleVariantChange();
              }
            });
            
            // Timeout fallback
            setTimeout(() => {
              if (unsubscribe) unsubscribe();
              handleVariantChange();
            }, 1000);
          } else {
            // Fallback: just wait a bit and proceed
            setTimeout(handleVariantChange, 300);
          }
        } else {
          // Could not select St, but allow submission (fallback)
          console.warn('B2B Pricing: Could not select St variant, allowing original submission');
          // Mark form as resubmitting to skip validation on next submit
          resubmittingForms.add(form);
          // Re-allow the form submission
          form.requestSubmit();
          resolve(true);
        }
      } else {
        // Variant is correct, allow submission
        resolve(true);
      }
    });
  }

  // Track forms that are being resubmitted after correction to prevent double validation
  const resubmittingForms = new WeakSet();

  // Setup form submission validation
  function setupFormValidation() {
    // Find all product forms
    const productForms = document.querySelectorAll('product-form');
    
    productForms.forEach(productFormElement => {
      const form = productFormElement.querySelector('form');
      if (!form) return;

      // Find the product-info element
      const productInfoElement = productFormElement.closest('product-info');
      if (!productInfoElement) return;

      // Check if this is a product form (not other forms)
      if (!form.dataset.type || form.dataset.type !== 'add-to-cart-form') {
        return; // Not a product form, skip
      }

      // Intercept form submission in capture phase (before product-form.js handler)
      form.addEventListener('submit', async (event) => {
        // Skip validation if this is a resubmission after correction
        if (resubmittingForms.has(form)) {
          resubmittingForms.delete(form);
          return; // Allow the resubmission to proceed normally
        }

        // Validate variant before allowing submission
        await validateVariantBeforeSubmit(productFormElement, productInfoElement, event);
      }, true); // Use capture phase to intercept before product-form.js handler
    });
  }

  // Main initialization function
  function init() {
    // Find all product-info elements on the page
    const productInfoElements = document.querySelectorAll('product-info');
    
    productInfoElements.forEach(productInfoElement => {
      // Initialize on page load
      initializeB2BPricing(productInfoElement);

      // Listen for variant changes to maintain pricingType selection
      const variantSelectsElement = productInfoElement.querySelector('variant-selects');
      if (variantSelectsElement) {
        // Listen to option value selection changes
        variantSelectsElement.addEventListener('change', (event) => {
          // Only re-apply if the changed option is NOT pricingType
          const changedOption = event.target.closest('fieldset, .product-form__input');
          const pricingTypeOption = findPricingTypeOption(variantSelectsElement);
          
          if (changedOption !== pricingTypeOption) {
            // Small delay to ensure variant change is processed
            setTimeout(() => {
              maintainPricingTypeSelection(productInfoElement);
            }, 100);
          }
        });
      }
    });

    // Setup form validation
    setupFormValidation();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize when product-info element is loaded/updated (for AJAX updates)
  document.addEventListener('product-info:loaded', (event) => {
    if (event.target && event.target.matches('product-info')) {
      setTimeout(() => {
        initializeB2BPricing(event.target);
        // Re-setup form validation for new product forms
        setupFormValidation();
      }, 100);
    }
  });

  // Also listen for variant change events from pub/sub system
  // Wait for pub/sub to be available (scripts load with defer)
  function setupPubSubListener() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
        // Find the product-info element that contains this variant selector
        const variantSelectsElement = event.data?.target?.closest('variant-selects');
        if (variantSelectsElement) {
          const productInfoElement = variantSelectsElement.closest('product-info');
          if (productInfoElement) {
            // Check if the changed option is NOT pricingType
            const changedOption = event.data?.target?.closest('fieldset, .product-form__input');
            const pricingTypeOption = findPricingTypeOption(variantSelectsElement);
            
            if (changedOption !== pricingTypeOption) {
              setTimeout(() => {
                maintainPricingTypeSelection(productInfoElement);
              }, 100);
            }
          }
        }
      });
    } else {
      // Retry after a short delay if pub/sub not yet loaded
      setTimeout(setupPubSubListener, 100);
    }
  }

  // Setup pub/sub listener when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupPubSubListener);
  } else {
    setupPubSubListener();
  }

})();
